/**
 * phaseSections — the ONE sentence a step view uses to say where the run is.
 *
 * Story 26739. The step views replace "Step 4 of 9" with a sentence a person
 * can act on. This module owns that sentence, and it owns the rule that the
 * sentence NEVER CONTAINS A NUMBER: a counter tells the reader how much of
 * something they cannot see is left, which is the anxiety the whole surface
 * exists to remove. The counter still exists — inside "All phases", where
 * somebody who wants it has asked for it (design decision D4).
 *
 * Resolution order, most-authored to least:
 *
 *   1. the workflow's own published `ui_metadata.sections[<phase id>]` — the
 *      author of the workflow said what this phase is called on this surface;
 *   2. the server's `phaseLabel` from the phase model — a real display name
 *      the substrate published for the phase the pointer is on;
 *   3. a local map over the run's STATUS — the fallback for a run whose
 *      workflow declares no grouping, an instance that predates the phase
 *      model, or a pointer sitting on the exempt sign-in spine.
 *
 * Never invents a phase name. A phase the server carried with no display name
 * arrives from `phaseModel` already spelled `"<id> (name not reported)"`, and
 * THAT is not a sentence — so step (2) rejects it and falls through to (3)
 * rather than showing the reader a step id.
 *
 * Pure: no React, no Office.js, no Remix server import (design Constraint 20).
 */

import type { PhaseStreamModel } from "../phaseModel";
import { NAME_NOT_REPORTED_SUFFIX } from "../phaseModel";

/**
 * The status-level fallback sentences.
 *
 * PROPOSED, PENDING RATIFICATION (design decision D8,
 * `tasks/26735-decision-ui-design.md`). Written in ora's voice — present
 * tense, first person plural, no jargon and no number.
 */
export const SECTION_SENTENCES = {
  /** Signing in, before any work has started. */
  signing_in: "Signing in to Epic",
  /** Signing in again, mid-run (a cold resume, a dead page, a re-entry). */
  signing_in_again: "Signing back in to Epic",
  /** Blocked on a person approving the sign-in challenge. */
  awaiting_mfa: "Waiting for you to approve the sign-in",
  /** The ordinary running sentence. */
  working: "Working in Epic",
  /** Parked on a person. */
  waiting_on_you: "Waiting on you",
  /** Parked on the carrier. */
  waiting_on_carrier: "Waiting on the carrier",
  /** Everything is done except sending the reply. */
  ready_to_send: "Ready for you to send the reply",
  /** Finished cleanly. */
  done: "Done",
  /** Finished, but not cleanly. */
  stopped: "Stopped",
  /** Status not loaded yet — never "not started". */
  loading: "Checking where this run is",
} as const;

export type SectionSentence =
  (typeof SECTION_SENTENCES)[keyof typeof SECTION_SENTENCES];

/** The published per-phase section names, keyed by phase id. */
export type CatalogSections = Record<string, string> | null | undefined;

/**
 * Read `ui_metadata.sections` off a catalog entry without requiring either
 * host's concrete catalog type. The add-in's `WorkflowCatalogEntry` does not
 * declare `ui_metadata` at all today, and ora's declares it with a different
 * member set — so this reads the bag defensively and returns `null` for
 * anything that is not a string→string map.
 */
export function readCatalogSections(catalogEntry: unknown): CatalogSections {
  if (!catalogEntry || typeof catalogEntry !== "object") return null;
  const meta = (catalogEntry as Record<string, unknown>).ui_metadata;
  if (!meta || typeof meta !== "object") return null;
  const sections = (meta as Record<string, unknown>).sections;
  if (!sections || typeof sections !== "object" || Array.isArray(sections)) {
    return null;
  }
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(sections as Record<string, unknown>)) {
    if (typeof value === "string" && value.trim() !== "") out[key] = value.trim();
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** The id of the phase the pointer is on, or null. */
export function currentPhaseId(
  model: PhaseStreamModel | null | undefined,
): string | null {
  const row = model?.rows?.find((r) => r.current);
  return row?.id ?? null;
}

/** A server label is usable only if it is a real name, not a spelled-out id. */
function usableServerLabel(label: string | null | undefined): string | null {
  if (typeof label !== "string") return null;
  const trimmed = label.trim();
  if (trimmed === "") return null;
  if (trimmed.endsWith(NAME_NOT_REPORTED_SUFFIX)) return null;
  return trimmed;
}

function fallbackSentence(
  model: PhaseStreamModel | null | undefined,
  status: string | null | undefined,
): string {
  // The exempt sign-in spine is its own answer regardless of status: "in the
  // login sequence" is NOT "not started", and the model already says which
  // sign-in state it is.
  if (model?.stepExempt) {
    switch (model.signInState) {
      case "awaiting_mfa":
        return SECTION_SENTENCES.awaiting_mfa;
      case "again":
        return SECTION_SENTENCES.signing_in_again;
      case "first":
      default:
        return SECTION_SENTENCES.signing_in;
    }
  }

  if (status == null) return SECTION_SENTENCES.loading;

  switch (status) {
    case "running":
      return SECTION_SENTENCES.working;
    case "paused_hitl":
    case "warm_hitl":
    case "rpa_review":
      return SECTION_SENTENCES.waiting_on_you;
    case "pending_edocs":
      return SECTION_SENTENCES.waiting_on_carrier;
    case "pending_email_send":
      return SECTION_SENTENCES.ready_to_send;
    case "completed":
      return SECTION_SENTENCES.done;
    case "failed":
    case "rejected":
    case "cancelled":
    case "timeout_cancelled":
      return SECTION_SENTENCES.stopped;
    default:
      // A status this surface has never heard of is still a running workflow
      // as far as the reader is concerned. `pickDrawerStatusFamily` owns the
      // dev warning for it; repeating one here would double every log line.
      return SECTION_SENTENCES.working;
  }
}

/**
 * The section sentence for a run.
 *
 * @param model    the phase-stream model, or null when the host has none.
 * @param status   the run's live status string, or null while it loads.
 * @param sections the workflow's published `ui_metadata.sections` map — pass
 *                 `readCatalogSections(catalogEntry)`. Optional so a host that
 *                 has no catalog entry to hand (both notification landings)
 *                 still gets a sentence rather than nothing.
 */
export function sectionLabelFor(
  model: PhaseStreamModel | null | undefined,
  status: string | null | undefined,
  sections?: CatalogSections,
): string {
  const phaseId = currentPhaseId(model);
  if (phaseId && sections) {
    const authored = sections[phaseId];
    if (typeof authored === "string" && authored.trim() !== "") {
      return authored.trim();
    }
  }

  // A run on the exempt sign-in spine has no phase and no milestone; its
  // `phaseLabel` is null by construction, so this falls through — but be
  // explicit, because "Signing in" beats a stale previous phase name.
  if (!model?.stepExempt) {
    const server = usableServerLabel(model?.phaseLabel);
    if (server) return server;
  }

  return fallbackSentence(model, status);
}

export default sectionLabelFor;
